// ============================================================================
// Component-scoped Record Collaboration data owner
// ============================================================================
//
// CommentsSection and RecordActivityLog are rendered by twenty-four distinct
// record surfaces, but they are not route data. They are a reusable record
// capability: the identity is the authenticated subject plus the record entity
// and, for mention profiles, the audience policy. Putting these requests in
// routeDataCatalog would falsely make every authenticated surface a consumer.
//
// RecordCollaborationSection is therefore the only owner of these mount reads.
// It hands settled data and deliberate refetch commands to its presentational
// descendants. The action-time comment write/edit/delete and attachment/edit
// history reads remain in CommentsSection, where their UI actions originate.
// ============================================================================

import {skipToken, useQuery} from '@tanstack/react-query';
import {useCallback, useMemo, useRef} from 'react';

import type {AuthState} from '../contexts/AuthContext.js';
import {listActivityEvents} from '../lib/activityApi.js';
import {listComments, loadMentionableProfiles} from '../lib/commentsApi.js';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import {RECORD_COLLABORATION_MENTION_POLICY, RECORD_COLLABORATION_RESOURCE} from './recordCollaborationRegistry.js';
import type {RecordCollaborationMentionPolicy} from './recordCollaborationRegistry.js';
// These are identity utilities, not a route-resource declaration. No
// collaboration resource appears in routeDataCatalog's CATALOG or in the 77
// surface manifest: the dedicated registry records only the record mounts that
// actually compose this shared capability.
import {resourceKey, scopeOrNull, UNRESOLVED_RESOURCE_KEY} from '../app/data/routeDataCatalog.js';
import type {QueryScope} from '../app/data/routeDataCatalog.js';

// Re-export the identity constants from the small registry so a caller needs
// one import for the hook and its policy. The registry itself has no query
// runtime dependency, which keeps the receipt/proof independently readable.
export {RECORD_COLLABORATION_MENTION_POLICY, RECORD_COLLABORATION_RESOURCE};
export type {RecordCollaborationMentionPolicy};

// Every supported loader takes an optional abort signal. A unary existing
// loader remains assignable and simply ignores the second argument; the two
// built-in RPC wrappers consume it so an abandoned record does not continue a
// request after Query has lost its last observer.
export type RecordCollaborationMentionLoader = (
  sb: SupabaseClient,
  options?: {signal?: AbortSignal},
) => Promise<unknown[]>;

const EMPTY_COLLECTION: unknown[] = [];

type CollectionProjection = {
  data: unknown[];
  loading: boolean;
  error: unknown;
};

export type RecordCollaborationData = {
  comments: CollectionProjection;
  mentionableProfiles: CollectionProjection;
  activity: CollectionProjection;
  creation: {
    createdAt: string | null;
    createdBy: string | null;
    createdByName: string | null;
    loading: boolean;
  };
  refreshComments: () => Promise<unknown>;
  refreshActivity: () => Promise<unknown>;
};

type RecordCollaborationDataInput = {
  sb: SupabaseClient;
  authState: AuthState;
  entityType: string | null | undefined;
  entityId: string | null | undefined;
  showComments: boolean;
  activityLimit: number;
  mentionPolicy?: RecordCollaborationMentionPolicy;
  loadMentionable?: RecordCollaborationMentionLoader;
  recordCreatedAt?: string | null;
  recordCreatedBy?: string | null;
  recordCreatedByName?: string | null;
};

export function recordCollaborationScope(authState: AuthState): QueryScope | null {
  // Collaboration RPCs are authenticated-only. The old component happened to
  // call them before an accidental signed-out mount could be noticed; this
  // owner deliberately parks until a resolved signed-in identity exists.
  if (!authState || !authState.user) return null;
  // AuthContext carries the active preview role in `role` and marks it. Passing
  // that value to scopeOrNull distinguishes an administrator previewing light
  // access from a genuinely light account with the same effective role.
  const previewRole = authState.rolePreviewActive ? authState.role : '';
  return scopeOrNull(authState, previewRole);
}

function queryKey(
  resourceId: (typeof RECORD_COLLABORATION_RESOURCE)[keyof typeof RECORD_COLLABORATION_RESOURCE],
  scope: QueryScope | null,
  params: Readonly<Record<string, unknown>>,
): readonly unknown[] {
  return scope ? resourceKey(resourceId, scope, params) : UNRESOLVED_RESOURCE_KEY;
}

function collectionProjection(
  query: {data: unknown[] | undefined; isPending: boolean; isFetching: boolean; error: unknown},
  enabled: boolean,
): CollectionProjection {
  return {
    data: Array.isArray(query.data) ? query.data : EMPTY_COLLECTION,
    // Keep the existing record visible during a same-key refresh. A new record
    // key has no data, so it correctly renders the loading placeholder instead
    // of another record's comments or audit count.
    loading: Boolean(enabled && (query.isPending || (query.isFetching && query.data === undefined))),
    error: query.error ?? null,
  };
}

function useGuardedRefetch(enabled: boolean, refetch: () => Promise<unknown>): () => Promise<unknown> {
  // Record descendants hold these callbacks in event listeners. Keep their
  // identity stable while still reading the current Query observer/refetch
  // function and refusing Query's otherwise-permitted disabled refetch.
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  return useCallback(() => (enabledRef.current ? refetchRef.current().catch(() => undefined) : Promise.resolve()), []);
}

function resolveMentionSource(
  policy: RecordCollaborationMentionPolicy | undefined,
  loader: RecordCollaborationMentionLoader | undefined,
): {policy: RecordCollaborationMentionPolicy; loader: RecordCollaborationMentionLoader} {
  if (!loader) {
    if (policy && policy !== RECORD_COLLABORATION_MENTION_POLICY.ALL_AUTHORIZED) {
      throw new Error('RecordCollaborationSection: a non-default mention policy requires its registered loader');
    }
    return {policy: RECORD_COLLABORATION_MENTION_POLICY.ALL_AUTHORIZED, loader: loadMentionableProfiles};
  }
  if (policy !== RECORD_COLLABORATION_MENTION_POLICY.TODO_PARTICIPANTS) {
    throw new Error('RecordCollaborationSection: a custom mention loader must declare the todo-participants policy');
  }
  return {policy, loader};
}

/**
 * The one component-scoped owner for RecordCollaborationSection's mount reads.
 * It intentionally is not a route-resource hook: calling it says a persisted
 * record mounted the collaboration capability, not that a page surface owns
 * every shared record stream.
 */
export function useRecordCollaborationData({
  sb,
  authState,
  entityType,
  entityId,
  showComments,
  activityLimit,
  mentionPolicy,
  loadMentionable,
  recordCreatedAt,
  recordCreatedBy,
  recordCreatedByName,
}: RecordCollaborationDataInput): RecordCollaborationData {
  const scope = useMemo(() => recordCollaborationScope(authState), [authState]);
  const mentionSource = useMemo(
    () => resolveMentionSource(mentionPolicy, loadMentionable),
    [mentionPolicy, loadMentionable],
  );
  const entityReady = Boolean(scope && entityType && entityId);
  const commentsEnabled = Boolean(entityReady && showComments);

  const commentsQuery = useQuery<unknown[]>({
    queryKey: queryKey(RECORD_COLLABORATION_RESOURCE.COMMENTS, scope, {
      entityType: entityType || null,
      entityId: entityId || null,
      limit: 100,
    }),
    // Destructuring signal is load-bearing: TanStack Query only aborts an
    // orphaned request if the query function reads it.
    queryFn: commentsEnabled
      ? ({signal}) => listComments(sb, entityType || '', entityId || '', {limit: 100, signal})
      : skipToken,
    enabled: commentsEnabled,
  });

  const mentionableProfilesQuery = useQuery<unknown[]>({
    queryKey: queryKey(RECORD_COLLABORATION_RESOURCE.MENTIONABLE_PROFILES, scope, {
      mentionPolicy: mentionSource.policy,
    }),
    queryFn: commentsEnabled ? ({signal}) => mentionSource.loader(sb, {signal}) : skipToken,
    enabled: commentsEnabled,
  });

  const activityQuery = useQuery<unknown[]>({
    queryKey: queryKey(RECORD_COLLABORATION_RESOURCE.ACTIVITY, scope, {
      entityType: entityType || null,
      entityId: entityId || null,
      limit: activityLimit,
    }),
    queryFn: entityReady
      ? ({signal}) => listActivityEvents(sb, entityType || '', entityId || '', {limit: activityLimit, signal})
      : skipToken,
    enabled: entityReady,
  });

  const refreshComments = useGuardedRefetch(commentsEnabled, commentsQuery.refetch);
  const refreshActivity = useGuardedRefetch(entityReady, activityQuery.refetch);

  return {
    comments: collectionProjection(commentsQuery, commentsEnabled),
    mentionableProfiles: collectionProjection(mentionableProfilesQuery, commentsEnabled),
    activity: collectionProjection(activityQuery, entityReady),
    creation: {
      createdAt: recordCreatedAt ?? null,
      createdBy: recordCreatedBy ?? null,
      createdByName: recordCreatedByName ?? null,
      loading: false,
    },
    refreshComments,
    refreshActivity,
  };
}
